import Ad from "@components/Advertisement/Ad";
import AdSettings from "@components/Advertisement/AdSettings";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import GAData from "@components/GAData/GAData";
import Header from "@components/Header/Header";
import MetaTags from "@components/MetaTags/MetaTags";
import {
  BT_NEWSLETTERS,
  NEWSLETTER_TYPE,
} from "@components/Newsletter/types/Newsletter";
import NewsletterSignUpPanel from "@components/Newsletter/Verticals/NewsletterSignUp";
import VerticalBlockTitle from "@components/VerticalBlockTitle/VerticalBlockTitle";
import PulseEntry from "@features/pulse/components/PulseEntry/PulseEntry";
import { ImagePresets } from "@pages/Article/components/ArticleThumbnail/utils/enums";
import VerticalsBanner from "@pages/Section/layouts/Verticals/components/VerticalsBanner";
import {
  VERTICAL_NEWSLETTER_COLORS_PROPS,
  VERTICAL_NEWSLETTER_DESC_COMPONENT,
} from "@pages/Section/layouts/Verticals/utils/constant";
import { SectionContext } from "@pages/Section/utils/types";
import { CardVariations } from "@particles/card/utils/types";
import Listing from "@particles/listing/Listing";
import { cn, toTitleCase } from "@util/helpers";

type DefaultSectionLayoutProps = Pick<SectionContext, "data">;

/**
 * Layout for Verticals Latest pages.
 * No figma
 */
const VerticalsLatestPage = ({
  data: { entity, title, overview, seoFooterData, relativePath },
}: DefaultSectionLayoutProps): React.ReactElement => {
  const { name, urlPath, uniqueName, parent } = entity;
  const gaTitle = urlPath.endsWith("/latest")
    ? `${parent?.name} Latest`
    : parent?.name;

  const variant: CardVariations = {
    kicker: {
      color: cn("text-gray-515"),
      extraClass: cn("font-poppins text-4xs font-light leading-6"),
    },
    image: {
      width: "w-4/10 md:w-2/10 lg:w-3/20",
      position: "right",
      aspectRatio: "aspect-3x2",
      orientation: "landscape",
      filters: { w: ImagePresets.MEDIUM, h: 600, dpr: 1, f: "webp" },
    },
    content: {
      width: "w-6/10 md:w-8/10 lg:17/20",
      extraClass: "pr-4",
      blurb: { extraClass: "text-gray-850", wrapperClassName: cn("lg:my-2") },
      updatedTimeColor: cn("text-gray-850"),
      updatedTimeExtraClass: cn("font-poppins text-7xs"),
      updatedTimeWrapperClass: cn("mt-2 lg:mt-auto"),
    },
    title: {
      size: "text-base",
      color: "text-gray-850",
      extraClass: cn("font-bold font-lct"),
    },
    subscriberLabel: { extraClass: "mt-2 mb-0" },
  };

  const NewsletterDescComponent =
    VERTICAL_NEWSLETTER_DESC_COMPONENT[relativePath];

  const newsletterProps = VERTICAL_NEWSLETTER_COLORS_PROPS[relativePath];

  return (
    <div className="w-full">
      <AdSettings
        adNames={["lb1", "prestitial", "catfish"]}
        path={relativePath}
        adTargets={[{ key: "page", value: "listing" }]}
      />

      <GAData
        chapter1={name || ""}
        level2={parent?.name || ""}
        title={toTitleCase(gaTitle || "")}
        adblocker={typeof window !== undefined ? 0 : window.canRunAds}
      />

      <MetaTags
        title={title}
        description="Read more at The Business Times."
        ogType="article"
        slug={relativePath}
      />

      <Ad
        adType="lb1"
        className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
      />

      <Header />

      <PulseEntry />

      {/* Top Stories */}
      <div className="container px-[var(--gutter-x-sm)] py-[var(--gutter-y)]">
        <VerticalsBanner relativePath={relativePath} className="my-6" />

        <VerticalBlockTitle
          id="asean-business-vertical-latest"
          rootClassName="mb-4"
          text="Latest"
        />

        <Listing
          cardProps={overview}
          urlPath={urlPath}
          pageType={"section"}
          size={10}
          sections={[uniqueName]}
          templateGCardVariantOverride={variant}
        />
      </div>

      <NewsletterSignUpPanel
        {...newsletterProps}
        rootClassName="mb-3"
        descriptionComponent={
          NewsletterDescComponent ? <NewsletterDescComponent /> : null
        }
        newsletter={BT_NEWSLETTERS.filter(
          (newsletter) =>
            newsletter.type === NEWSLETTER_TYPE.MORNING ||
            newsletter.type === NEWSLETTER_TYPE.EVENING
        )}
      />

      <SeoFooter data={seoFooterData} />

      <Ad adType="catfish" />

      <Ad adType="prestitial" />
    </div>
  );
};

export default VerticalsLatestPage;
